import { useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import ToggleButton from "core/buttons/toggle-button/ToggleButton";
import Button from "core/buttons/button/Button";
import StepHeader from "./StepHeader";
import NeedForm from "../NeedForm";
import FafErrorDot from "../FafErrorDot";
import FafBudgetWarning from "../FafBudgetWarning";
import { useUserContext } from "api/user/UserProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { emptyNeed } from "utils/fafHelper";
import { FAF_LEGACY_NEED_CLIENT_PLACEHOLDER } from "constants/constants";
import { translateHelper } from "utils/helper";

export const MAX_NEW_NEEDS = 3;

// Step 5 — needs: review of existing needs (still_active) + creation (max 3).
const NeedsStep = ({
  reviewNeeds,
  clientLabelById,
  reviewMap,
  setReview,
  newNeeds,
  setNewNeeds,
  showNewNeed,
  setShowNewNeed,
  reviewError,
  needErrors,
  needBudgetReached,
}) => {
  // Needs to review: list filtered by the wizard (excluding needs from the
  // current declaration, which are handled as "being added").
  // The label of a need reads from both referentials: the job (`settings_jobs`)
  // and its category. Since redmine 12940 a need only carries the job.
  const { lang } = useUserContext();
  const { jobList, jobListGetter } = useJobContext();
  const { jobCategoryByJobId, jobCategoryListGetter } = useJobCategoryContext();

  // One effect per referential: `jobListGetter` changes identity as soon as the
  // job list resolves, so sharing an effect would replay the other getter on
  // every resolution. (`jobCategoryListGetter` deliberately has no "already
  // loaded" guard — it refetches on purpose, so it must not be replayed.)
  useEffect(() => {
    jobListGetter();
  }, [jobListGetter]);

  useEffect(() => {
    jobCategoryListGetter();
  }, [jobCategoryListGetter]);

  const updateNeed = (index, patch) =>
    setNewNeeds((prev) =>
      prev.map((n, i) => (i === index ? { ...n, ...patch } : n))
    );

  // Removes an added need (e.g. by accident); if it was the last one, closes the section.
  const removeNeed = (index) => {
    const next = newNeeds.filter((_, i) => i !== index);
    setNewNeeds(next);
    if (next.length === 0) setShowNewNeed(false);
  };

  // "Category · Job" label of a need, from the job it carries (redmine 12940).
  const categoryLabel = (jobId) => {
    const job = jobList.find((j) => j.id === jobId);
    if (!job) return null;
    const jobLabel = translateHelper({ lang, fr: job.fr, en: job.en });
    const category = jobCategoryByJobId[jobId];
    if (!category) return jobLabel;
    const categoryLabelText = translateHelper({
      lang,
      fr: category.fr,
      en: category.en,
    });
    return `${categoryLabelText} · ${jobLabel}`;
  };

  return (
    <div className="faf-step">
      <StepHeader>
        <FormattedMessage
          id="tools_manager_faf_needs_title"
          defaultMessage="Tes besoins sont-ils toujours d'actualité ?"
        />
      </StepHeader>

      {reviewNeeds.map(({ need }) => {
        // Free text carries its own name, a reference client only its id.
        const freetext =
          need.client_name_freetext === FAF_LEGACY_NEED_CLIENT_PLACEHOLDER
            ? null
            : need.client_name_freetext;
        const clientName = freetext || clientLabelById?.get(need.client_id);
        const meta = [categoryLabel(need.job_id), clientName]
          .filter(Boolean)
          .join(" — ");
        const unreviewed = reviewMap[need.id] === undefined;
        return (
        <div
          className={`faf-list-card ${
            reviewError && unreviewed ? "faf-list-card--error" : ""
          }`}
          key={need.id}
        >
          <div className="faf-list-card-main">
            <div className="faf-list-card-title">
              <h4>{need.title}</h4>
              {reviewError && unreviewed && (
                <FafErrorDot
                  label={{
                    id: "tools_manager_faf_need_review_dot_label",
                    defaultMessage: "Besoin à statuer",
                  }}
                  errorMessage="tools_manager_faf_need_review_error_field"
                />
              )}
            </div>
            {meta && <div className="faf-list-card-meta">{meta}</div>}
          </div>
          <div className="faf-choice">
            <button
              type="button"
              className={`faf-choice-btn yes ${
                reviewMap[need.id] === true ? "on" : ""
              }`}
              onClick={() => setReview(need.id, true)}
              data-testid={`faf-needs-review-${need.id}-yes`}
            >
              <FormattedMessage
                id="tools_manager_faf_need_still_active"
                defaultMessage="Toujours d'actualité"
              />
            </button>
            <button
              type="button"
              className={`faf-choice-btn no ${
                reviewMap[need.id] === false ? "on" : ""
              }`}
              onClick={() => setReview(need.id, false)}
              data-testid={`faf-needs-review-${need.id}-no`}
            >
              <FormattedMessage
                id="tools_manager_faf_need_no_longer_active"
                defaultMessage="Plus d'actualité"
              />
            </button>
          </div>
        </div>
        );
      })}

      <div className="faf-add-need-row">
        <span className="faf-field-label">
          <FormattedMessage
            id="tools_manager_faf_need_add_question"
            defaultMessage="Ajouter un nouveau besoin ?"
          />{" "}
          ({newNeeds.length}/{MAX_NEW_NEEDS})
        </span>
        <ToggleButton
          id="faf-add-need"
          dataTestId="faf-needs-add-toggle"
          value={showNewNeed}
          onChange={(v) => {
            setShowNewNeed(v);
            if (v && newNeeds.length === 0) setNewNeeds([emptyNeed()]);
          }}
        />
      </div>

      {showNewNeed &&
        newNeeds.map((n, i) => (
          <NeedForm
            key={n.uid ?? i}
            id={i}
            need={n}
            onChange={(patch) => updateNeed(i, patch)}
            showErrors={needErrors?.[i]}
            onRemove={() => removeNeed(i)}
          />
        ))}

      {showNewNeed && needBudgetReached && <FafBudgetWarning />}

      {showNewNeed && (
        <Button
          size="medium"
          color="deep-purple"
          disabled={needBudgetReached}
          data-testid="faf-needs-add-another"
          onClick={() => setNewNeeds((prev) => [...prev, emptyNeed()])}
        >
          <FormattedMessage
            id="tools_manager_faf_need_add_another"
            defaultMessage="Ajouter un autre besoin"
          />
        </Button>
      )}
    </div>
  );
};

NeedsStep.propTypes = {
  reviewNeeds: PropTypes.array.isRequired,
  clientLabelById: PropTypes.instanceOf(Map),
  reviewMap: PropTypes.object.isRequired,
  setReview: PropTypes.func.isRequired,
  newNeeds: PropTypes.array.isRequired,
  setNewNeeds: PropTypes.func.isRequired,
  showNewNeed: PropTypes.bool.isRequired,
  setShowNewNeed: PropTypes.func.isRequired,
  reviewError: PropTypes.bool,
  needErrors: PropTypes.array,
  needBudgetReached: PropTypes.bool,
};

export default NeedsStep;
